import React, { ReactNode, useMemo } from 'react';
import { Group, Text } from '@mantine/core';
import StatusIndicator from '@/components/StatusIndicator.tsx';
import { StepType } from '@/types/automationFields.ts';
import { AutomationStep, TriggerType } from '@/types/automationSteps.ts';
import useStepIntegration from '@/hooks/useStepIntegration.ts';
import useGetStepTypeConfigAndLabel from '@/pages/Editor/components/EditorStepCard/hooks/useGetStepTypeConfigAndLabel.ts';
import StepCardIcon from '@/components/StepCard/StepCardIcon.tsx';
import { usePinnedStep } from '@/hooks/usePinnedStep.ts';
import AppIcon from '@/components/AppIcon.tsx';
import { TiPin } from 'react-icons/ti';
import { ExecutionStatus } from '@/types/statuses.ts';
import { StepExecutionTerminalReasonContext } from '@/types/stepExecutions.ts';
import { isStandaloneCapabilityProvider } from '@/utils/providerKinds.ts';

type Props = {
  step: AutomationStep;
  status?: ExecutionStatus;
  terminalReasonCode?: string | null;
  terminalReasonContext?: StepExecutionTerminalReasonContext | null;
  currentStepPath?: string;
  showPin?: boolean;
  showLabel?: boolean;
  iconSize?: number;
  runId?: string;
  statusAdornment?: ReactNode;
  isUpstreamSkipped?: boolean;
  allowConfiguredIntegrationFallback?: boolean;
};

export default function StepCardTitle({
  step,
  status,
  terminalReasonCode,
  terminalReasonContext,
  currentStepPath,
  showPin = false,
  showLabel = true,
  iconSize,
  runId,
  statusAdornment,
  isUpstreamSkipped = false,
  allowConfiguredIntegrationFallback = false,
}: Props) {
  const { isStepPinned } = usePinnedStep(step, runId);

  const integrationConfig = useMemo(() => {
    if (
      (step.step_type === StepType.Trigger && step.trigger_type === TriggerType.External) ||
      step?.step_type === StepType.Dispatch
    ) {
      return step.integration_config;
    }
    return undefined;
  }, [step]);

  const { integrations, selectedIntegrationProvider, selectedIntegrationAction, loadingIntegrations } =
    useStepIntegration(
      step.step_type,
      integrationConfig?.integration_id || null,
      integrationConfig?.action_type || null,
    );

  const resolvedIntegrationProvider = useMemo(() => {
    if (selectedIntegrationProvider) {
      return selectedIntegrationProvider;
    }
    if (!allowConfiguredIntegrationFallback || !integrationConfig?.integration_type) {
      return undefined;
    }
    return integrations.find((provider) => provider.name === integrationConfig.integration_type);
  }, [
    allowConfiguredIntegrationFallback,
    integrationConfig?.integration_type,
    integrations,
    selectedIntegrationProvider,
  ]);

  const resolvedIntegrationAction = useMemo(() => {
    if (selectedIntegrationAction) {
      return selectedIntegrationAction;
    }
    if (!resolvedIntegrationProvider || !integrationConfig?.action_type) {
      return undefined;
    }
    const actions =
      step.step_type === StepType.Trigger
        ? resolvedIntegrationProvider.trigger_actions
        : resolvedIntegrationProvider.dispatch_actions;
    return actions.find((action) => action.name === integrationConfig.action_type);
  }, [
    integrationConfig?.action_type,
    resolvedIntegrationProvider,
    selectedIntegrationAction,
    step.step_type,
  ]);

  const { config, label } = useGetStepTypeConfigAndLabel(step.step_type, resolvedIntegrationAction);

  const cta = useMemo(() => {
    if (![StepType.Trigger, StepType.Dispatch].includes(step.step_type)) {
      return config.label;
    } else if (step.step_type === StepType.Trigger && step.trigger_type === TriggerType.Manual) {
      return 'Manual Trigger';
    } else if (step.step_type === StepType.Trigger && step.trigger_type === TriggerType.Scheduled) {
      return 'Scheduled Trigger';
    } else if (loadingIntegrations) {
      return '';
    } else if (resolvedIntegrationProvider && resolvedIntegrationAction) {
      if (isStandaloneCapabilityProvider(resolvedIntegrationProvider)) {
        return `${resolvedIntegrationAction.display_name}`;
      } else {
        return `${resolvedIntegrationProvider.display_name}: ${resolvedIntegrationAction.display_name}`;
      }
    } else if (resolvedIntegrationProvider && !resolvedIntegrationAction) {
      return `${resolvedIntegrationProvider.display_name}: + Add an action`;
    } else if (step.step_type === StepType.Trigger) {
      return '+ Add a trigger';
    } else {
      return '+ Select an integration';
    }
  }, [
    config.label,
    resolvedIntegrationAction,
    resolvedIntegrationProvider,
    loadingIntegrations,
    step,
  ]);

  return (
    <Group align="center" wrap={'nowrap'}>
      <StepCardIcon
        Icon={config.icon}
        CustomIcon={config.svg}
        bg={config.iconBackgroundColor}
        iconColor={config.iconColor}
        integrationProvider={resolvedIntegrationProvider}
        size={iconSize}
      />

      {showLabel && (
        <div>
          <Group gap={'4px'}>
            <Text size="xs" c="dimmed">
              {label}
            </Text>
            {isStepPinned && showPin && <AppIcon Icon={TiPin} size={14} color="#FF761F" />}
          </Group>
          <Group wrap="nowrap" gap="6px" align={'center'}>
            <Text
              size="sm"
              fw={500}
              style={{ whiteSpace: 'normal', wordBreak: 'break-word', flex: 1 }}
            >
              {cta}
            </Text>
            {!!status && (
              <StatusIndicator
                status={status}
                terminalReasonCode={terminalReasonCode}
                terminalReasonContext={terminalReasonContext}
                currentStepPath={currentStepPath}
                isUpstreamSkipped={isUpstreamSkipped}
                include_text={false}
              />
            )}
            {statusAdornment}
          </Group>
        </div>
      )}
    </Group>
  );
}
